import * as THREE from 'three';
import { BaseEnemy } from './base.js';
import { ENEMY_STATS } from '../../data/enemies.data.js';

export class Zilua extends BaseEnemy {
  constructor(opts) {
    super({ ...opts, stats: ENEMY_STATS.zilua });
    this._summonCd = this.stats.summonCooldown * (0.5 + Math.random() * 0.35);
    this._summons = new Set();
    this._strafeDir = Math.random() < 0.5 ? 1 : -1;
    this._strafeTimer = 0.6 + Math.random() * 0.9;
  }

  buildMesh() {
    super.buildMesh();

    const mantle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.75, 0.45, 1.05, 8),
      new THREE.MeshStandardMaterial({
        color: this.stats.bodyColor,
        roughness: 0.8,
        metalness: 0.05,
      })
    );
    mantle.position.y = 0.65;
    mantle.castShadow = true;
    this.group.add(mantle);

    const orb = new THREE.Mesh(
      new THREE.SphereGeometry(0.22, 12, 12),
      new THREE.MeshStandardMaterial({
        color: this.stats.accentColor,
        emissive: this.stats.accentColor,
        emissiveIntensity: 1.1,
        roughness: 0.2,
      })
    );
    orb.position.set(0, 1.95, 0.2);
    this.group.add(orb);
    this._orb = orb;
  }

  _pruneSummons() {
    for (const summon of this._summons) {
      if (!summon?.alive || summon?.canDispose?.()) this._summons.delete(summon);
    }
  }

  _trySummon(game) {
    this._pruneSummons();
    const maxActive = this.stats.summonMaxActive ?? 4;
    if (this._summons.size >= maxActive) return;

    const batch = this.stats.summonBatchSize ?? 2;
    const available = Math.max(0, maxActive - this._summons.size);
    const tries = Math.min(batch, available);
    let spawnedAny = false;
    for (let i = 0; i < tries; i++) {
      const minmon = game.spawnSummonedEnemy('minmon', {
        sourceEnemy: this,
        minPlayerDistance: 3,
        minRadius: this.stats.summonMinRadius ?? 1.1,
        maxRadius: this.stats.summonMaxRadius ?? 3,
      });
      if (minmon) {
        this._summons.add(minmon);
        spawnedAny = true;
      }
    }
    if (spawnedAny && this._orb) {
      this._orb.material.emissiveIntensity = 2.1;
    }
  }

  updateAI(dt, game) {
    const player = game.player;
    if (!player.alive) return;

    this.sensePlayer(game, dt);
    this._summonCd = Math.max(0, this._summonCd - dt);

    const targetX = player.x;
    const targetZ = player.z;
    const dx = targetX - this.x;
    const dz = targetZ - this.z;
    const d = Math.hypot(dx, dz) || 0.0001;
    this.aimX = dx / d;
    this.aimZ = dz / d;

    const kiteRange = this.stats.attackRange * 0.72;
    this._strafeTimer -= dt;
    if (this._strafeTimer <= 0) {
      this._strafeDir = Math.random() < 0.5 ? 1 : -1;
      this._strafeTimer = 0.45 + Math.random() * 1;
    }

    if (!this._lastCanSeePlayer) {
      this.state = 'track';
      this.moveWithPathfinding(targetX, targetZ, dt, game, 0.95);
    } else if (d < kiteRange * 0.78) {
      this.state = 'kite';
      this.moveToward(this.x - this.aimX * 2.8, this.z - this.aimZ * 2.8, dt, game.room.walls, 1.05);
      this.aimX = dx / d;
      this.aimZ = dz / d;
    } else if (d > this.stats.attackRange * 0.98) {
      this.state = 'chase';
      this.moveToward(targetX, targetZ, dt, game.room.walls, 0.9);
    } else {
      const sideX = -this.aimZ * this._strafeDir;
      const sideZ = this.aimX * this._strafeDir;
      this.state = 'strafe';
      this.moveToward(this.x + sideX * 2, this.z + sideZ * 2, dt, game.room.walls, 0.82);
      this.aimX = dx / d;
      this.aimZ = dz / d;
    }

    if (this._lastCanSeePlayer && this.fireCd <= 0) {
      const spread = (Math.random() * 2 - 1) * 0.08;
      const angle = Math.atan2(this.aimX, this.aimZ) + spread;
      const shotDx = Math.sin(angle);
      const shotDz = Math.cos(angle);
      game.spawnEnemyProjectile(
        this.x + shotDx * 0.7,
        this.z + shotDz * 0.7,
        shotDx,
        shotDz,
        {
          speed: this.stats.projectileSpeed,
          damage: this.stats.projectileDamage,
          life: 1.5,
        }
      );
      this.playFireRecoilAnim();
      this.fireCd = this.stats.fireCooldown;
    }

    if (this._summonCd <= 0) {
      this._trySummon(game);
      this._summonCd = this.stats.summonCooldown * (0.82 + Math.random() * 0.36);
    }

    if (this._orb) {
      const pulse = 0.92 + Math.sin(this.stateTime * 7) * 0.22;
      this._orb.material.emissiveIntensity = Math.max(this._orb.material.emissiveIntensity * 0.9, pulse);
    }
  }
}
